import React, { useMemo, useState } from "react";

// Popup "✏️ Correggi codici" (vedi OrdiniMapping.jsx/Revisione.jsx, icona
// 🔢⚠️ nell'intestazione): su un file da migliaia di righe non si può
// chiedere di correggere le celle segnalate una per una — qui si vedono i
// valori DISTINTI (non riga per riga: lo stesso codice sbagliato di solito
// compare su tante taglie/righe dello stesso articolo) con una proposta di
// correzione già pronta — aggiunge gli zeri finali mancanti (zero perso da
// Excel) e/o toglie il testo in più dopo il codice (es. "99.718163
// (cotton)" → "99.718163", un'annotazione che il file cliente porta dietro
// ma che l'ERP non riconosce come parte del codice articolo) — modificabile
// a mano prima di applicarla, e un pulsante per applicarla a TUTTE le righe
// con quel valore in un colpo solo, oppure a tutti i valori della colonna
// insieme.
function suggerisciCorrezione(g, cifreAttese) {
  const m = String(g.valoreOriginale ?? "").match(/^(\d+)([.,])(\d+)/);
  if (!m) return g.valoreOriginale;
  const cifreMancanti = cifreAttese - g.cifreTrovate;
  // Il testo dopo il codice (es. "(cotton)") si toglie SEMPRE: non fa mai
  // parte del codice articolo vero. Le cifre si completano con zeri SOLO se
  // ne mancano — se sono già giuste o addirittura in eccesso (un'altra
  // cifra sbagliata, non un semplice zero perso) si tocca solo il testo in
  // più e si lascia la parte numerica come sta, da correggere a mano.
  return cifreMancanti > 0 ? `${m[0]}${"0".repeat(cifreMancanti)}` : m[0];
}

function raggruppaAnomalie(info, righe, colonna) {
  const gruppi = new Map(); // valoreOriginale -> { valoreOriginale, cifreTrovate, testoExtra, righe: [i,...] }
  for (const [i, { cifreTrovate, testoExtra }] of info.righe) {
    const valore = String(righe[i]?.[colonna] ?? "");
    if (!gruppi.has(valore)) gruppi.set(valore, { valoreOriginale: valore, cifreTrovate, testoExtra, righe: [] });
    gruppi.get(valore).righe.push(i);
  }
  return [...gruppi.values()].sort((a, b) => b.righe.length - a.righe.length);
}

function SezioneColonna({ colonna, info, righe, onApplica }) {
  const [modifiche, setModifiche] = useState({}); // { valoreOriginale: testoModificatoAMano }
  const [filtro, setFiltro] = useState("");
  const [applicando, setApplicando] = useState(false);

  const gruppi = useMemo(() => raggruppaAnomalie(info, righe, colonna), [info, righe, colonna]);
  const gruppiFiltrati = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    if (!f) return gruppi;
    return gruppi.filter((g) => g.valoreOriginale.toLowerCase().includes(f));
  }, [gruppi, filtro]);

  function valoreCorrente(g) {
    if (g.valoreOriginale in modifiche) return modifiche[g.valoreOriginale];
    return suggerisciCorrezione(g, info.cifreAttese);
  }

  async function applicaGruppo(g) {
    const nuovo = valoreCorrente(g);
    if (!nuovo || nuovo === g.valoreOriginale) return;
    setApplicando(true);
    try {
      await onApplica(colonna, [{ valoreOriginale: g.valoreOriginale, valoreNuovo: nuovo }]);
      setModifiche((prev) => { const c = { ...prev }; delete c[g.valoreOriginale]; return c; });
    } finally {
      setApplicando(false);
    }
  }

  async function applicaTutti() {
    const correzioni = gruppi
      .map((g) => ({ valoreOriginale: g.valoreOriginale, valoreNuovo: valoreCorrente(g) }))
      .filter((c) => c.valoreNuovo && c.valoreNuovo !== c.valoreOriginale);
    if (correzioni.length === 0) return;
    setApplicando(true);
    try {
      await onApplica(colonna, correzioni);
      setModifiche({});
    } finally {
      setApplicando(false);
    }
  }

  const applicabili = gruppi.filter((g) => valoreCorrente(g) !== g.valoreOriginale).length;
  const totaleRigheInteressate = gruppi.reduce((tot, g) => tot + g.righe.length, 0);

  return (
    <div style={{ marginBottom: "22px", paddingBottom: "18px", borderBottom: "1px solid var(--cf-border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
        <div>
          <strong style={{ fontFamily: "'Roboto Mono', Consolas, monospace" }}>{colonna}</strong>{" "}
          <span className="testo-secondario">
            — {gruppi.length} valori distinti, {totaleRigheInteressate} righe interessate ({info.cifreAttese} cifre attese)
          </span>
        </div>
        {applicabili > 0 && (
          <button className="btn-primario" onClick={applicaTutti} disabled={applicando}>
            {applicando ? "Applico..." : `Applica correzione a tutte (${applicabili})`}
          </button>
        )}
      </div>
      {gruppi.length > 6 && (
        <input
          type="text"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="🔍 filtra i valori..."
          style={{ width: "100%", boxSizing: "border-box", marginBottom: "8px" }}
        />
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "280px", overflowY: "auto" }}>
        {gruppiFiltrati.map((g) => {
          const cifreInEccesso = g.cifreTrovate > info.cifreAttese;
          return (
            <div
              key={g.valoreOriginale}
              style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 8px", borderRadius: "6px", backgroundColor: "var(--cf-bg-subtle)", border: "1px solid var(--cf-border)", fontSize: "13px" }}
            >
              <span
                style={{ fontFamily: "'Roboto Mono', Consolas, monospace", textDecoration: "line-through", color: "var(--cf-text-muted)", flexShrink: 0, maxWidth: "13rem", overflow: "hidden", textOverflow: "ellipsis" }}
                title={`${g.cifreTrovate} cifre trovate${g.testoExtra ? ` — testo in più: "${g.testoExtra}"` : ""}`}
              >
                {g.valoreOriginale}
              </span>
              <span style={{ color: "var(--cf-text-faint)", flexShrink: 0 }}>→</span>
              <input
                type="text"
                value={valoreCorrente(g)}
                onChange={(e) => setModifiche((prev) => ({ ...prev, [g.valoreOriginale]: e.target.value }))}
                disabled={applicando}
                style={{ flex: "1 1 auto", minWidth: "8rem", fontFamily: "'Roboto Mono', Consolas, monospace" }}
                title={cifreInEccesso ? "Questo valore ha PIÙ cifre del previsto: la parte numerica va corretta a mano, non è un semplice zero mancante" : undefined}
              />
              <span className="testo-secondario" style={{ flexShrink: 0 }}>{g.righe.length} riga/e</span>
              <button onClick={() => applicaGruppo(g)} disabled={applicando || valoreCorrente(g) === g.valoreOriginale}>
                Applica
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CorreggiCodiciModal({ colonneAnomale, righe, onApplica, onChiudi }) {
  const colonne = [...colonneAnomale.entries()];

  return (
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "Inter, sans-serif" }}
      onClick={onChiudi}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: "var(--cf-bg-card)", borderRadius: "16px", width: "720px", maxWidth: "94vw", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.45)" }}
      >
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #7f1d1d 100%)", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px", lineHeight: 1 }}>🔢⚠️</span>
            <div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontWeight: 700, fontSize: "15px", letterSpacing: "1px", textTransform: "uppercase", color: "#f1f5f9" }}>
                Correggi codici
              </div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "11px", color: "var(--cf-text-faint)", letterSpacing: "0.3px", marginTop: "3px" }}>
                Correzione in blocco, per valore — non riga per riga
              </div>
            </div>
          </div>
          <button className="cf-btn" onClick={onChiudi} style={{ border: "none", background: "rgba(255,255,255,0.08)", borderRadius: "6px", width: "28px", height: "28px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", color: "#cbd5e1", lineHeight: 1 }}>
            ✕
          </button>
        </div>

        <div style={{ padding: "24px", overflowY: "auto" }}>
          {colonne.length === 0 ? (
            <p className="testo-secondario" style={{ marginTop: 0 }}>
              ✅ Nessun codice sospetto rimasto in nessuna colonna.
            </p>
          ) : (
            <>
              <p className="testo-secondario" style={{ marginTop: 0 }}>
                Per ogni valore sbagliato: a sinistra quello trovato nel file, a destra la correzione proposta —
                zero finale aggiunto se mancava una cifra, ed eventuale testo in più dopo il codice (es. "(cotton)")
                sempre tolto — modificabile se non va bene. "Applica" lo scrive su tutte le righe che hanno quel
                valore in questa colonna. I valori corretti spariscono dalla lista.
              </p>
              {colonne.map(([colonna, info]) => (
                <SezioneColonna key={colonna} colonna={colonna} info={info} righe={righe} onApplica={onApplica} />
              ))}
            </>
          )}
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--cf-border)", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button onClick={onChiudi}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}
